import { useEffect, useState, useMemo } from 'react';
import { Image as ImageIcon, Plus, Trash2, ChevronDown, AlertCircle, Info, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ImageEntry } from '@/lib/api';

export interface ImageMetadataEntry extends ImageEntry {
  id: string;
  parentUrl: string;
}

interface ImageMetadataManagerProps {
  domain: string;
  entries: ImageMetadataEntry[];
  onChange: (entries: ImageMetadataEntry[]) => void;
  /** URLs the user already uploaded, used to surface a quick-pick list. */
  knownUrls: string[];
}

const STORAGE_PREFIX = 'sh.images.';

function isValidUrl(href: string): boolean {
  try {
    const u = new URL(href);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export function loadImageMetadata(domain: string): ImageMetadataEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + domain);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (e: unknown): e is ImageMetadataEntry =>
          !!e && typeof (e as ImageMetadataEntry).parentUrl === 'string' && typeof (e as ImageMetadataEntry).loc === 'string'
      )
      .map((e) => ({ ...e, id: e.id || crypto.randomUUID() }));
  } catch {
    return [];
  }
}

/**
 * Group image entries by their parent URL and strip the editor-only fields
 * (id, parentUrl) so the resulting `ImageEntry[]` is shaped exactly as the
 * worker /generate endpoint expects.
 */
export function buildImagesIndex(
  entries: ImageMetadataEntry[]
): Map<string, ImageEntry[]> {
  const idx = new Map<string, ImageEntry[]>();
  for (const entry of entries) {
    if (!isValidUrl(entry.parentUrl) || !isValidUrl(entry.loc)) continue;
    const existing = idx.get(entry.parentUrl) || [];
    existing.push({
      loc: entry.loc,
      title: entry.title || undefined,
      caption: entry.caption || undefined,
      geo_location: entry.geo_location || undefined,
      license: entry.license || undefined,
    });
    idx.set(entry.parentUrl, existing);
  }
  return idx;
}

function emptyEntry(parentUrl = ''): ImageMetadataEntry {
  return {
    id: crypto.randomUUID(),
    parentUrl,
    loc: '',
    title: '',
    caption: '',
    geo_location: '',
    license: '',
  };
}

export function ImageMetadataManager({
  domain,
  entries,
  onChange,
  knownUrls,
}: ImageMetadataManagerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeParent, setActiveParent] = useState<string | null>(null);

  useEffect(() => {
    try {
      if (entries.length === 0) {
        localStorage.removeItem(STORAGE_PREFIX + domain);
      } else {
        localStorage.setItem(STORAGE_PREFIX + domain, JSON.stringify(entries));
      }
    } catch {
      // localStorage unavailable — silent fallback.
    }
  }, [entries, domain]);

  // Default the "active parent" to the first URL the user has uploaded,
  // so adding an image just works without a manual URL paste.
  useEffect(() => {
    if (!activeParent && knownUrls.length > 0) {
      setActiveParent(knownUrls[0]);
    }
  }, [knownUrls, activeParent]);

  const grouped = useMemo(() => {
    const m = new Map<string, ImageMetadataEntry[]>();
    for (const entry of entries) {
      const list = m.get(entry.parentUrl) || [];
      list.push(entry);
      m.set(entry.parentUrl, list);
    }
    return m;
  }, [entries]);

  const validation = useMemo(() => {
    const issues: { id: string; message: string }[] = [];
    for (const entry of entries) {
      if (!isValidUrl(entry.parentUrl)) {
        issues.push({ id: entry.id, message: 'Parent URL is invalid' });
      }
      if (!entry.loc) {
        issues.push({ id: entry.id, message: 'Image URL is required' });
      } else if (!isValidUrl(entry.loc)) {
        issues.push({ id: entry.id, message: 'Image URL is invalid' });
      }
    }
    // Per Google spec, max 1000 images per URL — flag if any parent exceeds it.
    for (const [parent, list] of grouped) {
      if (list.length > 1000) {
        issues.push({
          id: list[0].id,
          message: `${parent} has ${list.length} images — Google's limit is 1000 per URL`,
        });
      }
    }
    return issues;
  }, [entries, grouped]);

  const summary = useMemo(() => {
    const parents = new Set(entries.map((e) => e.parentUrl).filter(Boolean));
    return { parentCount: parents.size, imageCount: entries.length };
  }, [entries]);

  const addEntry = () => {
    const parent = activeParent || knownUrls[0] || '';
    onChange([...entries, emptyEntry(parent)]);
  };
  const removeEntry = (id: string) => onChange(entries.filter((e) => e.id !== id));
  const updateEntry = (id: string, patch: Partial<ImageMetadataEntry>) =>
    onChange(entries.map((e) => (e.id === id ? { ...e, ...patch } : e)));

  return (
    <div className="bg-white rounded-xl border">
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
      >
        <div className="flex items-center gap-3">
          <ImageIcon className="w-5 h-5 text-pink-600" />
          <div className="text-left">
            <h3 className="font-semibold">Image metadata</h3>
            <p className="text-sm text-slate-500">
              {summary.imageCount === 0
                ? 'Optional — attach titles, captions, geo, and license to images for richer indexing in Google Images'
                : `${summary.imageCount} image${summary.imageCount === 1 ? '' : 's'} across ${summary.parentCount} URL${summary.parentCount === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>
        <ChevronDown className={cn('w-5 h-5 transition-transform', isExpanded && 'rotate-180')} />
      </button>

      {isExpanded && (
        <div className="p-4 border-t space-y-4">
          <div className="flex items-start gap-2 p-3 bg-pink-50 border border-pink-100 rounded-lg text-sm text-pink-900">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              Add image entries for any of your URLs. SitemapHost emits the standard{' '}
              <code className="bg-white px-1 rounded">image:image</code> namespace tags. Title, caption,
              geo, and license are all optional but help Google Images index your content correctly.
              Up to 1000 images per URL.{' '}
              <a
                href="https://developers.google.com/search/docs/crawling-indexing/sitemaps/image-sitemaps"
                target="_blank"
                rel="noopener noreferrer"
                className="underline inline-flex items-center gap-0.5"
              >
                Google docs
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          {entries.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">
              No image entries yet. Click "Add image" below to attach one to a URL.
            </div>
          )}

          {[...grouped.entries()].map(([parent, list]) => (
            <div key={parent} className="border rounded-lg p-3 space-y-3">
              <div className="text-sm font-mono text-slate-700 break-all">
                {parent || <span className="text-red-600">⚠ no parent URL set</span>}
              </div>
              {list.map((entry) => {
                const entryIssues = validation.filter((v) => v.id === entry.id);
                return (
                  <div key={entry.id} className="border rounded p-2 space-y-2 bg-slate-50">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Image</span>
                      <button
                        type="button"
                        onClick={() => removeEntry(entry.id)}
                        className="text-slate-400 hover:text-red-600 p-1"
                        title="Remove image"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <input
                      type="url"
                      value={entry.parentUrl}
                      onChange={(e) => updateEntry(entry.id, { parentUrl: e.target.value.trim() })}
                      placeholder="Parent URL"
                      className="w-full px-2 py-1.5 border rounded text-sm font-mono bg-white focus:outline-none focus:ring-2 focus:ring-pink-200"
                      list="known-urls"
                    />
                    <input
                      type="url"
                      value={entry.loc}
                      onChange={(e) => updateEntry(entry.id, { loc: e.target.value.trim() })}
                      placeholder="https://example.com/image.jpg"
                      className="w-full px-2 py-1.5 border rounded text-sm font-mono bg-white focus:outline-none focus:ring-2 focus:ring-pink-200"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={entry.title || ''}
                        onChange={(e) => updateEntry(entry.id, { title: e.target.value })}
                        placeholder="Title (optional)"
                        className="px-2 py-1.5 border rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-pink-200"
                      />
                      <input
                        type="text"
                        value={entry.caption || ''}
                        onChange={(e) => updateEntry(entry.id, { caption: e.target.value })}
                        placeholder="Caption (optional)"
                        className="px-2 py-1.5 border rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-pink-200"
                      />
                      <input
                        type="text"
                        value={entry.geo_location || ''}
                        onChange={(e) => updateEntry(entry.id, { geo_location: e.target.value })}
                        placeholder="Geo location (optional)"
                        className="px-2 py-1.5 border rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-pink-200"
                      />
                      <input
                        type="url"
                        value={entry.license || ''}
                        onChange={(e) => updateEntry(entry.id, { license: e.target.value })}
                        placeholder="License URL (optional)"
                        className="px-2 py-1.5 border rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-pink-200"
                      />
                    </div>
                    {entryIssues.length > 0 && (
                      <div className="space-y-1">
                        {entryIssues.map((issue, i) => (
                          <div key={i} className="flex items-start gap-1.5 text-xs text-red-700">
                            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>{issue.message}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          <datalist id="known-urls">
            {knownUrls.slice(0, 200).map((url) => (
              <option key={url} value={url} />
            ))}
          </datalist>

          <div className="flex items-center gap-2">
            {knownUrls.length > 0 && (
              <select
                value={activeParent ?? ''}
                onChange={(e) => setActiveParent(e.target.value)}
                className="text-sm border rounded-lg px-2 py-2 max-w-xs"
              >
                {knownUrls.slice(0, 200).map((url) => (
                  <option key={url} value={url}>
                    {url}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={addEntry}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              <Plus className="w-4 h-4" />
              Add image
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
